import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { businessReportsApi } from '@/api/businessReports';
import { downloadBlob } from '@/lib/downloadBlob';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

type RevenueExportKind =
  | 'breakdown'
  | 'patient_revenue'
  | 'billing_detail'
  | 'opd_lines'
  | 'daycare_lines'
  | 'pharma_lines'
  | 'lab_lines'
  | 'additional_services_lines';

export default function CaRevenueReports() {
  const [bucket, setBucket] = useState<'day' | 'month'>('day');
  const [exporting, setExporting] = useState<RevenueExportKind | null>(null);
  const {
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange,
  } = useReportDateRange('this_month');

  const { data: breakdown, isLoading } = useQuery({
    queryKey: ['ca-revenue-breakdown', fromIso, toIso, bucket],
    queryFn: () =>
      businessReportsApi.revenueBreakdown({
        from: fromIso!,
        to: toIso!,
        bucket,
      }),
    enabled: !!fromIso && !!toIso,
  });

  const dateSuffix =
    fromIso && toIso
      ? `${format(new Date(fromIso), 'yyyy-MM-dd')}-to-${format(new Date(toIso), 'yyyy-MM-dd')}`
      : 'report';

  const runExport = async (kind: RevenueExportKind) => {
    if (!fromIso || !toIso) return;
    setExporting(kind);
    try {
      let resp;
      let filename = `Revenue-${kind}-${dateSuffix}.csv`;
      switch (kind) {
        case 'breakdown':
          resp = await businessReportsApi.revenueBreakdownExport({ from: fromIso, to: toIso, bucket });
          filename = `Revenue-Breakdown-${bucket}-${dateSuffix}.csv`;
          break;
        case 'patient_revenue':
          resp = await businessReportsApi.patientRevenueAllExport({ from: fromIso, to: toIso });
          break;
        case 'billing_detail':
          resp = await businessReportsApi.patientBillingDetailExport({ from: fromIso, to: toIso, type: 'All' });
          break;
        case 'opd_lines':
          resp = await businessReportsApi.opdPaymentLinesExport({ from: fromIso, to: toIso });
          break;
        case 'daycare_lines':
          resp = await businessReportsApi.dayCarePaymentLinesExport({ from: fromIso, to: toIso });
          break;
        case 'pharma_lines':
          resp = await businessReportsApi.pharmaRevenueLinesExport({ from: fromIso, to: toIso });
          filename = `Pharma-billing-detailed-${dateSuffix}.csv`;
          break;
        case 'lab_lines':
          resp = await businessReportsApi.labBillingLinesExport({ from: fromIso, to: toIso });
          filename = `Lab-billing-detailed-${dateSuffix}.csv`;
          break;
        case 'additional_services_lines':
          resp = await businessReportsApi.additionalServicesBillingExport({ from: fromIso, to: toIso });
          filename = `Additional-services-billing-${dateSuffix}.csv`;
          break;
      }
      downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), filename);
      toast.success('Download started');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const totals = breakdown?.totals;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Revenue reports</h2>
        <p className="text-slate-400">Billed revenue by service type for the selected period.</p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
        <TimeIntervalFilter
          onTimeIntervalChange={handleTimeIntervalChange}
          selectedInterval={selectedInterval}
          onIntervalChange={setSelectedInterval}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Group by</span>
            <Select value={bucket} onValueChange={(v) => setBucket(v as 'day' | 'month')}>
              <SelectTrigger className="w-[120px] bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="month">Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {[
            { label: 'OPD', value: totals.opd },
            { label: 'Lab', value: totals.lab },
            { label: 'Pharma', value: totals.pharma },
            { label: 'Additional Services', value: totals.additionalServices ?? 0 },
            { label: 'IPD', value: totals.ipd },
            { label: 'Day care', value: totals.dc },
          ].map((t) => (
            <div key={t.label} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-400">{t.label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-white">
                ₹{t.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <h3 className="mb-3 text-sm font-medium text-slate-300">Download CSV</h3>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ['breakdown', 'Revenue breakdown'],
              ['patient_revenue', 'Patient-wise revenue'],
              ['billing_detail', 'Patient billing detail'],
              ['opd_lines', 'OPD payment lines'],
              ['daycare_lines', 'Day care payment lines'],
              ['pharma_lines', 'Pharma billing (detailed)'],
              ['lab_lines', 'Lab billing (detailed)'],
              ['additional_services_lines', 'Additional Services (detailed)'],
            ] as const
          ).map(([kind, label]) => (
            <Button
              key={kind}
              variant="outline"
              size="sm"
              className="border-white/15 bg-white/5 text-white hover:bg-white/10"
              disabled={!fromIso || !toIso || exporting === kind}
              onClick={() => runExport(kind)}
            >
              <Download className="mr-2 h-4 w-4" />
              {exporting === kind ? 'Exporting…' : label}
            </Button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        <div className="border-b border-white/10 px-4 py-3">
          <h3 className="font-medium text-white">Revenue breakdown series</h3>
        </div>
        {isLoading ? (
          <p className="p-4 text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Period</TableHead>
                  <TableHead className="text-right text-slate-400">OPD</TableHead>
                  <TableHead className="text-right text-slate-400">Lab</TableHead>
                  <TableHead className="text-right text-slate-400">Pharma</TableHead>
                  <TableHead className="text-right text-slate-400">Addl. Services</TableHead>
                  <TableHead className="text-right text-slate-400">IPD</TableHead>
                  <TableHead className="text-right text-slate-400">Day care</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(breakdown?.series ?? []).map((row) => (
                  <TableRow key={row.bucket} className="border-white/10">
                    <TableCell className="text-white">{row.bucket}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.opd.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.lab.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.pharma.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{(row.additionalServices ?? 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.ipd.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.dc.toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
                {(!breakdown?.series || breakdown.series.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      No revenue data for this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
